/**
 * The /app/workflow2-list route component (Workflow overhaul P2, 2026-10-10).
 *
 * Resume doc: Documents/workflow-ui-overhaul.md
 *
 * Renders EXACTLY ONE page:
 *   redesign/WorkflowsListPage.vue  the redesigned list (design 1a / 1b / 1t)
 *   WorkflowList.vue                the classic list, NOT edited
 * decided by shouldUseNewFollowupsUi('workflow', { isSuperAdmin, storage, userKey }): at stage `preview` a
 * Super Admin gets the new page unless they picked "Classic view" (localStorage teamio_workflow_ui = 'old')
 * and everyone else gets the classic one. Later stages and the office "classic Teamio" choice are the
 * gate's business (src/utils/followups/uiVersion.js), not this file's.
 *
 * ⛔ `userKey` MUST be passed. It is the employee ID the office choice is stored under; without it a stage
 * past `preview` would ignore a person's own "classic Teamio" choice. The customer store keeps that ID in
 * `state.customer.userID` (set from the site's EmployeeID at login).
 *
 * The Classic / New link follows the release convention (src/utils/followups/uiSwitch.js): a click goes through
 * switchUi('workflow', target, { isSuperAdmin, userKey }) (a Super Admin re-renders in place, an office user's
 * switch reloads the page), and the classic-side link is FollowupsUiToggle with `pageKey: 'workflow'` rendered
 * UNCONDITIONALLY, because the toggle decides by role itself (Super Admin always; office user only while the
 * area is at 'invite' / 'default').
 *
 * Same shape as src/pages/PatientProfile/PatientProfileRoute.js: a .js render function, so each page stays
 * the page root it really is (the classic list has no wrapper that would need to be edited), and a
 * version counter that makes the gate re-read storage after a Classic / New click.
 */
import WorkflowList from './WorkflowList.vue';
import WorkflowsListPage from './redesign/WorkflowsListPage.vue';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import { shouldUseNewFollowupsUi, browserStorage } from '@/utils/followups/uiVersion.js';
import { switchUi } from '@/utils/followups/uiSwitch.js';

export default {
  name: 'WorkflowListRoute',

  data() {
    // Bumped on every Classic / New click so the gate re-reads storage.
    return { layoutVersion: 0 };
  },

  computed: {
    isSuperAdminUser() {
      const user = this.$store.state.customer && this.$store.state.customer.user;
      return !!(user && user.Role === 'Super Admin');
    },
    // The employee ID the office choice is stored under (a route component has no mixin).
    officeUserKey() {
      return this.$store.state.customer ? this.$store.state.customer.userID : null;
    },
    useNew() {
      // eslint-disable-next-line no-unused-expressions
      this.layoutVersion;
      return shouldUseNewFollowupsUi('workflow', {
        isSuperAdmin: this.isSuperAdminUser,
        storage: browserStorage(),
        userKey: this.officeUserKey,
      });
    },
  },

  methods: {
    // A Super Admin re-renders in place; an office user's switch reloads the page (uiSwitch.js).
    setLayout(target) {
      const how = switchUi('workflow', target, { isSuperAdmin: this.isSuperAdminUser, userKey: this.officeUserKey });
      if (how === 'rerender') this.layoutVersion += 1;
    },
  },

  render(h) {
    if (this.useNew) {
      return h(WorkflowsListPage, {
        key: 'new',
        on: { 'switch-layout': this.setLayout },
      });
    }
    const children = [];
    // The way to the new page from the classic one. FollowupsUiToggle decides by role: always shown to a Super
    // Admin, shown to an office user only while the area is at 'invite' / 'default'.
    children.push(h(FollowupsUiToggle, {
      key: 'to-new',
      style: { marginBottom: '10px' },
      props: { target: 'new', pageKey: 'workflow', noteText: 'Super Admin: a redesigned Workflows page is ready to check.' },
      on: { toggle: this.setLayout },
    }));
    children.push(h(WorkflowList, { key: 'old' }));
    return h('div', { attrs: { 'data-test': 'workflow-list-classic' } }, children);
  },
};
